import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/common/Layout'
import ShareCTA from '../components/familyboard/ShareCTA'
import WisdomThreadsSection from '../components/familyboard/WisdomThreadsSection'
import ActionStrip from '../components/familyboard/ActionStrip'
import ActiveFilterBar from '../components/familyboard/ActiveFilterBar'
import FilterDrawer from '../components/familyboard/FilterDrawer'
import { useFamilyBoardData } from '../hooks/useFamilyBoardData'
import { useFamily } from '../contexts/FamilyContext'

/**
 * FamilyBoard Page
 *
 * The primary senior dashboard showing:
 * - Wisdom Threads (new conversational view with search/filter/sort)
 * - "Your Family Needs You" section (waiting items)
 * - "Recent Family Moments" section
 * - Share CTA
 * - Stats bar
 *
 * Uses cream background and senior-friendly typography.
 */
function FamilyBoard() {
  const navigate = useNavigate()
  const { activeFamilyId, families } = useFamily()
  const { data, loading, error } = useFamilyBoardData(activeFamilyId, families)

  // Search, filter, sort state
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPeople, setSelectedPeople] = useState(new Set())
  const [sortBy, setSortBy] = useState('newest')
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

  const handleMomentAction = (moment) => {
    navigate(`/stories/${moment.id}`)
  }

  const handleItemAction = (item) => {
    navigate(`/stories/${item.id}`)
  }

  const handleCapsuleView = (capsule) => {
    navigate(`/timecapsule`)
  }

  const handleShareStory = () => {
    navigate('/tell-a-story')
  }

  const handleThreadAction = (thread) => {
    const storyId = thread.id.replace('story-', '').replace('question-', '')
    navigate(`/stories/${storyId}`)
  }

  const handleStartThread = (threadType) => {
    if (threadType === 'time_capsule') {
      navigate('/timecapsule')
    } else {
      navigate('/tell-a-story')
    }
  }

  const handleViewStory = (story) => {
    navigate(`/stories/${story.id}`)
  }

  // Build allPeople array for FilterDrawer (current user + connections)
  const allPeople = useMemo(() => {
    if (!data) return []
    const people = []
    if (data.user?.id) {
      people.push({
        id: data.user.id,
        firstName: data.user.firstName,
        lastName: data.user.lastName || '',
        relationship: 'You',
        avatarColor: data.user.avatarColor || '#6B7F4A',
        storyCount: data.user.storyCount || 0,
        isCurrentUser: true,
      })
    }
    ;(data.connections || []).forEach(conn => {
      people.push({
        id: conn.id,
        firstName: conn.firstName,
        lastName: conn.lastName || '',
        relationship: conn.relationship || 'Family',
        avatarColor: conn.avatarColor || '#6B7F4A',
        storyCount: conn.storyCount || 0,
        isCurrentUser: false,
      })
    })
    return people
  }, [data])

  // Filter → search → sort stories
  const processedStories = useMemo(() => {
    if (!data?.stories) return []
    let result = [...data.stories]

    // Filter by selected people
    if (selectedPeople.size > 0) {
      result = result.filter(story =>
        story.sharer && selectedPeople.has(story.sharer.id)
      )
    }

    // Search by title
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase()
      result = result.filter(story =>
        (story.title || '').toLowerCase().includes(query)
      )
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.sharedAt || 0) - new Date(b.sharedAt || 0)
        case 'most_comments':
          return (b.engagement?.commentCount || 0) - (a.engagement?.commentCount || 0)
        case 'most_reactions':
          return (b.engagement?.reactionCount || 0) - (a.engagement?.reactionCount || 0)
        case 'newest':
        default:
          return new Date(b.sharedAt || 0) - new Date(a.sharedAt || 0)
      }
    })

    return result
  }, [data?.stories, selectedPeople, searchTerm, sortBy])

  // People objects for ActiveFilterBar chips
  const selectedPeopleDetails = useMemo(() => {
    return allPeople.filter(p => selectedPeople.has(p.id))
  }, [allPeople, selectedPeople])

  const handleRemovePerson = (personId) => {
    setSelectedPeople(prev => {
      const next = new Set(prev)
      next.delete(personId)
      return next
    })
  }

  const handleClearAllFilters = () => {
    setSelectedPeople(new Set())
  }

  const handleFilterApply = (newSelected) => {
    setSelectedPeople(newSelected)
  }

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fb-olive mx-auto mb-4"></div>
                <p className="font-serif text-gray-600 text-lg">Loading your family board...</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  // Error state
  if (error) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
              <span className="text-5xl mb-4 block">😔</span>
              <h3 className="font-serif font-bold text-xl text-gray-800 mb-2">
                Something went wrong
              </h3>
              <p className="font-serif text-gray-500 text-base mb-4">
                We couldn't load your family board. Please try again.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-fb-olive hover:bg-fb-olive/90 text-white font-bold py-2 px-6 rounded-lg"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  // No data state
  if (!data) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <span className="text-5xl mb-4 block">👋</span>
              <h3 className="font-serif font-bold text-xl text-gray-800 mb-2">
                Welcome to your Family Board
              </h3>
              <p className="font-serif text-gray-500 text-base">
                Start by inviting family members to connect.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen bg-fb-cream">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Welcome header with view toggle */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-serif font-bold text-2xl text-gray-900">
                Welcome back, {data.user.firstName}
              </h1>
              <p className="font-serif text-gray-600 text-base mt-1">
                {families.length > 1
                  ? `Stories from all ${families.length} families you're connected to`
                  : "Here's what's happening with your family"
                }
              </p>
            </div>

          </div>

          {/* Main content - Wisdom Threads View */}
          <div className="space-y-6">
                {/* Action Strip: Search + Filter + Sort */}
                <ActionStrip
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  selectedPeopleCount={selectedPeople.size}
                  onFilterClick={() => setFilterDrawerOpen(true)}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                />

                {/* Active Filter Bar */}
                <ActiveFilterBar
                  selectedPeople={selectedPeopleDetails}
                  filteredStoryCount={processedStories.length}
                  onRemovePerson={handleRemovePerson}
                  onClearAll={handleClearAllFilters}
                />

                {/* Share CTA */}
                <ShareCTA onShareStory={handleShareStory} />

                <WisdomThreadsSection
                  useNewLayout={true}
                  stories={processedStories}
                  onViewStory={handleViewStory}
                  onStartThread={handleStartThread}
                />

                {/* Filter Drawer */}
                <FilterDrawer
                  isOpen={filterDrawerOpen}
                  onClose={() => setFilterDrawerOpen(false)}
                  people={allPeople}
                  selectedPeopleIds={selectedPeople}
                  onApply={handleFilterApply}
                />

          </div>
        </div>
      </div>
    </Layout>
  )
}

export default FamilyBoard
